import { useCallback } from 'react';
import { Panel } from '@/components/Panel';
import { CanvasChart } from '@/components/CanvasChart';
import { useHealthScore } from '@/hooks/useHealthScore';
import { Spinner, EmptyState } from '@00start/ui';
import { SIGNAL, drawGridLines, formatTime } from '@/utils/chart-helpers';

const PAD = { top: 12, right: 12, bottom: 28, left: 36 };

export function HealthScoreChart() {
  const { scores, loading } = useHealthScore(24);

  const onDraw = useCallback(
    (ctx: CanvasRenderingContext2D, w: number, h: number) => {
      if (!scores.length) return;

      drawGridLines(ctx, w, h, PAD, 0, 100, 5);

      const plotW = w - PAD.left - PAD.right;
      const plotH = h - PAD.top - PAD.bottom;
      const barW = Math.max(2, plotW / scores.length - 1);

      scores.forEach((pt, i) => {
        const x = PAD.left + (i / scores.length) * plotW;
        const barH = (pt.score / 100) * plotH;
        const y = PAD.top + plotH - barH;

        if (pt.score < 60) {
          ctx.fillStyle = SIGNAL.critical;
        } else if (pt.score < 80) {
          ctx.fillStyle = SIGNAL.warning;
        } else {
          ctx.fillStyle = 'rgba(148, 163, 184, 0.4)';
        }

        ctx.fillRect(x, y, barW, barH);

        // X axis time label every ~6th bar
        if (i % 6 === 0) {
          ctx.fillStyle = 'rgba(148, 163, 184, 0.5)';
          ctx.font = '10px SF Mono, monospace';
          ctx.textAlign = 'center';
          ctx.fillText(formatTime(pt.timestamp), x + barW / 2, h - PAD.bottom + 14);
        }
      });

      // Threshold line at 80%
      const y80 = PAD.top + plotH - (80 / 100) * plotH;
      ctx.setLineDash([4, 3]);
      ctx.lineWidth = 1;
      ctx.strokeStyle = 'rgba(148, 163, 184, 0.35)';
      ctx.beginPath();
      ctx.moveTo(PAD.left, y80);
      ctx.lineTo(w - PAD.right, y80);
      ctx.stroke();

      // Threshold line at 60%
      const y60 = PAD.top + plotH - (60 / 100) * plotH;
      ctx.beginPath();
      ctx.moveTo(PAD.left, y60);
      ctx.lineTo(w - PAD.right, y60);
      ctx.stroke();

      ctx.setLineDash([]);
    },
    [scores],
  );

  if (loading) {
    return (
      <Panel title="Health Score (24h)" mobilePriority={3}>
        <div className="flex justify-center py-4">
          <Spinner size="sm" />
        </div>
      </Panel>
    );
  }

  if (!scores.length) {
    return (
      <Panel title="Health Score (24h)" mobilePriority={3}>
        <EmptyState message="No health score data" />
      </Panel>
    );
  }

  return (
    <Panel title="Health Score (24h)" mobilePriority={3}>
      <CanvasChart onDraw={onDraw} height={180} />
    </Panel>
  );
}
